import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  classifyGstLookupError,
  isValidGstinFormat,
  lookupGstDetails,
} from "@/services/gstDetailsLookup";
import { useHydrationCount } from "./HydrationContext";

const LOOKUP_DEBOUNCE_MS = 400;

export const GST_ADDRESS_SOURCE_FIELD = "address_source";
export const GST_PINCODE_SOURCE_FIELD = "pincode_source";
const GST_ADDRESS_SOURCE = "gstin";

export function useGstAddressAutofill(index: number): void {
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext();
  const gstinField = `branches.${index}.gstin_primary`;
  const line1Field = `branches.${index}.facility_address_line_1`;
  const pincodeField = `branches.${index}.facility_pincode`;
  const cityField = `branches.${index}.facility_city`;
  const countryField = `branches.${index}.facility_country`;
  const sourceField = `branches.${index}.${GST_ADDRESS_SOURCE_FIELD}`;
  const pincodeSourceField = `branches.${index}.${GST_PINCODE_SOURCE_FIELD}`;
  const primaryField = `branches.${index}.is_primary_facility`;
  const raw = useWatch({ control, name: gstinField }) as string | undefined;
  const gstin = (raw ?? "").trim().toUpperCase();
  const isPrimary = useWatch({ control, name: primaryField }) === true;
  const hydrationCount = useHydrationCount();
  const lastSeenHydrationRef = useRef(hydrationCount);
  const previousGstinRef = useRef<string | null>(null);
  const previousPrimaryRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (hydrationCount !== lastSeenHydrationRef.current) {
      lastSeenHydrationRef.current = hydrationCount;
      previousGstinRef.current = gstin;
      previousPrimaryRef.current = isPrimary;
      return;
    }
    const firstRun = previousGstinRef.current === null;
    const changed =
      previousGstinRef.current !== gstin || previousPrimaryRef.current !== isPrimary;
    previousGstinRef.current = gstin;
    previousPrimaryRef.current = isPrimary;
    if (firstRun || !changed) return;
    const clear = (fields: string[]) => {
      for (const field of fields) {
        setValue(field, "", { shouldValidate: false, shouldDirty: true });
      }
    };
    const withdraw = () => {
      if (getValues(sourceField) === GST_ADDRESS_SOURCE) {
        clear([line1Field, sourceField]);
      }
      if (getValues(pincodeSourceField) === GST_ADDRESS_SOURCE) {
        clear([pincodeField, cityField, countryField, pincodeSourceField]);
      }
    };
    if (!isPrimary || !isValidGstinFormat(gstin)) {
      withdraw();
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await lookupGstDetails(gstin);
          if (cancelled) return;
          if (!result.valid) {
            setError(gstinField, {
              type: "gst-lookup",
              message: result.status
                ? `GSTIN is not active (status: ${result.status}).`
                : "GSTIN not found. Please re-check the number.",
            });
            withdraw();
            return;
          }
          clearErrors(gstinField);
          withdraw();
          const address = result.address;
          if (!address) return;
          if (address.line1) {
            setValue(line1Field, address.line1, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue(sourceField, GST_ADDRESS_SOURCE, { shouldDirty: true });
          }
          if (address.pincode) {
            setValue(pincodeField, address.pincode, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue(pincodeSourceField, GST_ADDRESS_SOURCE, { shouldDirty: true });
          }
        } catch (err) {
          if (cancelled) return;
          withdraw();
          const info = classifyGstLookupError(err);
          if (info.kind === "unavailable") return;
          setError(gstinField, { type: "gst-lookup", message: info.message });
        }
      })();
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    gstin,
    isPrimary,
    hydrationCount,
    gstinField,
    line1Field,
    pincodeField,
    cityField,
    countryField,
    sourceField,
    pincodeSourceField,
    setValue,
    getValues,
    setError,
    clearErrors,
  ]);
}
